// v5 — inline error handling (no alert())
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { sendNotification, getAdminIds, getBranchManagerEmails, getAdminEmails, sendEmail, emailToolTaggedOut } from '../lib/notify';
import MobileTagout from './MobileTagout';
import InlineMessage from '../components/InlineMessage';
import useMessage from '../lib/useMessage';

const STATUS_BADGE = {
  active: 'badge-active', tagged_out: 'badge-tagged', in_repair: 'badge-repair',
  in_transit: 'badge-transit', retired: 'badge-retired', spare: 'badge-spare',
  pending: 'badge-pending', available: 'badge-spare',
};
const STATUS_LABEL = {
  active: 'Active', tagged_out: 'Tagged out', in_repair: 'In repair',
  in_transit: 'In transit', retired: 'Retired', spare: 'Spare',
  pending: 'Pending receipt', available: 'Available',
};
const EVENT_LABEL = {
  added: 'Added to inventory', status_change: 'Status changed',
  tagged_out: 'Tagged out', receipt_confirmed: 'Receipt confirmed',
  edited: 'Record edited', spare_received: 'Received from Dallas spare pool',
};

function fmt$(v) {
  return v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
}
function fmtDate(ts) {
  return ts ? new Date(ts).toLocaleString() : '—';
}

export default function ToolDetailPage({ resourceType }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, isAdmin, isManager } = useAuth();
  const [tool, setTool] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const qrRef = useRef(null);

  // Desktop tagout modal
  const [showTagout, setShowTagout] = useState(false);
  const [tagForm, setTagForm] = useState({});
  const [saving, setSaving] = useState(false);

  const tagoutMsg = useMessage();

  // Mobile tagout
  const isMobile = window.innerWidth <= 768;
  const [showMobileTagout, setShowMobileTagout] = useState(false);

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);

      if (resourceType === 'tool') {
        const { data: toolData, error: toolError } = await supabase
          .from('tools')
          .select('*, branches(name), tool_models(msrp)')
          .eq('id', id)
          .maybeSingle();

        if (toolError || !toolData) {
          setError('Tool not found.');
          setLoading(false);
          return;
        }
        setTool(toolData);

        const { data: histData } = await supabase
          .from('tool_history')
          .select('*')
          .eq('tool_id', id)
          .order('created_at', { ascending: false });
        setHistory(histData || []);
      }
      setLoading(false);
    }
    load();
  }, [id, resourceType]);

  async function refreshTool() {
    const { data: updatedTool } = await supabase
      .from('tools').select('*, branches(name), tool_models(msrp)').eq('id', id).maybeSingle();
    if (updatedTool) setTool(updatedTool);
    const { data: histData } = await supabase
      .from('tool_history').select('*').eq('tool_id', id).order('created_at', { ascending: false });
    setHistory(histData || []);
  }

  async function saveTagout() {
    if (!tagForm.issue_description?.trim()) { tagoutMsg.showError('Issue description is required.'); return; }
    setSaving(true);
    tagoutMsg.clearMessage();
    const toolId = parseInt(tool.id);
    const oldStatus = tool.status;
    const { error: toolError } = await supabase.from('tools').update({ status: tagForm.repair_status }).eq('id', toolId);
    if (toolError) { tagoutMsg.showError('Error updating tool status: ' + toolError.message); setSaving(false); return; }
    const { error: logError } = await supabase.from('tagout_log').insert({
      tool_id: toolId, reason: tagForm.reason, issue_description: tagForm.issue_description,
      tagged_by_name: profile?.full_name, status: tagForm.repair_status,
      shop_name: tagForm.shop_name || null, estimated_repair_cost: tagForm.estimated_repair_cost || null,
    });
    if (logError) { tagoutMsg.showError('Error saving tag-out record: ' + logError.message); setSaving(false); return; }
    await supabase.from('tool_history').insert({
      tool_id: toolId, event_type: 'tagged_out', old_status: oldStatus,
      new_status: tagForm.repair_status, changed_by_name: profile?.full_name,
      changed_by_id: profile?.id, notes: `${tagForm.reason}: ${tagForm.issue_description}`,
      metadata: { shop_name: tagForm.shop_name, estimated_repair_cost: tagForm.estimated_repair_cost },
    });
    const adminIds = await getAdminIds();
    await sendNotification(adminIds,
      `Tool tagged out: ${tool.name}`,
      `${profile?.full_name} tagged out ${tool.name} at ${tool.branches?.name || 'unknown branch'}. Reason: ${tagForm.reason}.`,
      'tool_tagged_out'
    );
    const adminEmails = await getAdminEmails();
    const managerEmails = await getBranchManagerEmails(tool.branch_id);
    const allEmails = [...new Set([...adminEmails, ...managerEmails])];
    await sendEmail(allEmails, `Tool tagged out: ${tool.name}`,
      emailToolTaggedOut(tool.name, tool.branches?.name || 'unknown branch', tagForm.reason, profile?.full_name));
    setSaving(false);
    setShowTagout(false);
    await refreshTool();
  }

  function handleTagoutClick() {
    if (isMobile) {
      setShowMobileTagout(true);
    } else {
      setTagForm({ reason: 'Damage', issue_description: '', shop_name: '', estimated_repair_cost: '', repair_status: 'tagged_out' });
      tagoutMsg.clearMessage();
      setShowTagout(true);
    }
  }

  function printQR() {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 140; canvas.height = 140;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.drawImage(img, 0, 0);
      const pngUrl = canvas.toDataURL('image/png');
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html><head><title>QR - ${tool.name}</title>
        <style>
          @media print { @page { margin: 10mm; } }
          body { font-family: Arial, sans-serif; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; padding: 20px; box-sizing: border-box; }
          img.qr { width: 160px; height: 160px; margin-bottom: 12px; }
          .name { font-size: 16px; font-weight: 700; margin-bottom: 4px; text-align: center; }
          .sub { font-size: 12px; color: #555; margin-bottom: 3px; text-align: center; }
          .url { font-size: 9px; color: #999; text-align: center; word-break: break-all; max-width: 200px; margin-top: 6px; }
        </style></head>
        <body>
          <img class="qr" src="${pngUrl}" />
          <div class="name">${tool.name}</div>
          <div class="sub">${[tool.model_number, tool.serial_number ? 'S/N: ' + tool.serial_number : null].filter(Boolean).join(' · ') || ''}</div>
          <div class="sub">${[tool.branches?.name, tool.department, tool.station_truck ? 'Station ' + tool.station_truck : null].filter(Boolean).join(' · ')}</div>
          <div class="url">${window.location.origin}/tool/${tool.id}</div>
          <script>window.onload = () => { window.print(); }<\/script>
        </body></html>
      `);
      printWindow.document.close();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)' }}>
      <div className="flex-center gap-1"><div className="spinner" /><span style={{ color: 'var(--gray-lt)' }}>Loading...</span></div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--off-white)', padding: '1rem' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔧</div>
        <div style={{ fontFamily: 'var(--font-head)', fontSize: '20px', fontWeight: 700, marginBottom: '8px' }}>Tool not found</div>
        <div style={{ color: 'var(--gray-mid)', marginBottom: '24px' }}>This tool may have been removed or the QR code is outdated.</div>
        <button className="btn btn-primary" onClick={() => navigate('/')}>Go to fleet inventory</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--off-white)' }}>
      <header className="topbar" style={{ padding: isMobile ? '0 0.75rem' : '0 1.5rem' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')} style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '6px', border: 'none' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
          {isMobile ? '' : 'Fleet'}
        </button>
        <div style={{ flex: 1 }} />
        {!isMobile && <div style={{ color: 'white', fontSize: '13px', opacity: 0.7 }}>{profile?.full_name}</div>}
      </header>

      <div style={{ maxWidth: '680px', margin: '0 auto', padding: isMobile ? '12px 12px 100px' : '24px 16px' }}>

        {/* Tool identity card */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="card-body" style={{ padding: isMobile ? '14px' : '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-head)', fontSize: isMobile ? '20px' : '22px', fontWeight: 700, marginBottom: '4px', lineHeight: 1.2 }}>{tool.name}</div>
                <div style={{ color: 'var(--gray-mid)', fontSize: '13px' }}>
                  {[tool.model_number, tool.serial_number ? 'S/N: ' + tool.serial_number : null].filter(Boolean).join(' · ') || 'No model / serial on record'}
                </div>
              </div>
              <span className={`badge ${STATUS_BADGE[tool.status] || 'badge-active'}`} style={{ fontSize: '12px', padding: '4px 10px', flexShrink: 0 }}>
                {STATUS_LABEL[tool.status] || tool.status}
              </span>
            </div>
            {isMobile && tool.status === 'active' && (
              <button className="btn btn-danger" onClick={handleTagoutClick} style={{ width: '100%', marginTop: '14px' }}>
                ⚠ Tag out this tool
              </button>
            )}
          </div>
        </div>

        {/* Details grid */}
        <div className="card" style={{ marginBottom: '12px' }}>
          <div className="card-header" style={{ padding: isMobile ? '10px 14px' : '1rem 1.25rem' }}>
            <div className="card-title">Tool details</div>
          </div>
          <div className="card-body" style={{ padding: isMobile ? '14px' : '1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '12px' : '16px' }}>
              {[
                { label: 'Branch', value: tool.branches?.name },
                { label: 'Department', value: tool.department },
                { label: 'Station / truck', value: tool.station_truck },
                { label: 'MSRP', value: tool.tool_models?.msrp ? fmt$(tool.tool_models.msrp) : null },
                { label: 'Purchase cost', value: tool.cost ? fmt$(tool.cost) : null },
                { label: 'Acquisition', value: tool.acquisition_type !== 'unknown' ? tool.acquisition_type?.replace('_', ' ') : null },
              ].map(({ label, value }) => (
                <div key={label}>
                  <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: isMobile ? '13px' : '14px', color: value ? 'var(--black)' : 'var(--gray-lt)' }}>{value || '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* QR Code */}
        {(isAdmin || isManager) && (
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="card-header" style={{ padding: isMobile ? '10px 14px' : '1rem 1.25rem' }}>
              <div className="card-title">QR code</div>
            </div>
            <div className="card-body" style={{ padding: isMobile ? '14px' : '1.25rem', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '14px' : '24px', alignItems: isMobile ? 'center' : 'flex-start' }}>
              <div style={{ background: 'white', padding: '12px', border: '1px solid var(--border)', borderRadius: '8px', display: 'inline-block', flexShrink: 0 }}>
                <div ref={qrRef}>
                  <QRCodeSVG value={`${window.location.origin}/tool/${tool.id}`} size={isMobile ? 160 : 140} level="M" />
                </div>
              </div>
              <div style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>
                <div style={{ fontSize: '13px', color: 'var(--gray-mid)', marginBottom: '8px' }}>Scan to open this tool's record. Print and attach to the tool or its case.</div>
                <div style={{ fontSize: '11px', color: 'var(--gray-lt)', marginBottom: '14px', wordBreak: 'break-all' }}>{window.location.origin}/tool/{tool.id}</div>
                <button className="btn btn-primary btn-sm" onClick={printQR} style={isMobile ? { width: '100%' } : {}}>🖨️ Print QR label</button>
              </div>
            </div>
          </div>
        )}

        {/* Desktop action buttons */}
        {!isMobile && (isAdmin || isManager) && tool.status === 'active' && (
          <div className="card" style={{ marginBottom: '12px' }}>
            <div className="card-body" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button className="btn btn-danger" onClick={handleTagoutClick}>Tag out this tool</button>
            </div>
          </div>
        )}

        {/* History timeline */}
        <div className="card">
          <div className="card-header" style={{ padding: isMobile ? '10px 14px' : '1rem 1.25rem' }}>
            <div className="card-title">Tool history</div>
          </div>
          <div className="card-body" style={{ padding: isMobile ? '14px' : '1.25rem' }}>
            {history.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📋</div>
                <div className="empty-state-title">No history yet</div>
                <div className="empty-state-sub">History is recorded from this point forward.</div>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                {history.map((h, i) => (
                  <div key={h.id} style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', marginTop: '4px', flexShrink: 0, background: h.event_type === 'tagged_out' ? '#C62828' : h.event_type === 'receipt_confirmed' ? '#2E7D32' : h.event_type === 'added' ? '#1565C0' : '#757575' }} />
                      {i < history.length - 1 && <div style={{ width: '2px', flex: 1, background: '#E0E0E0', marginTop: '4px' }} />}
                    </div>
                    <div style={{ flex: 1, paddingBottom: '4px' }}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--black)' }}>{EVENT_LABEL[h.event_type] || h.event_type}</div>
                      {(h.old_status || h.new_status) && h.old_status !== h.new_status && (
                        <div style={{ fontSize: '12px', color: 'var(--gray-mid)', marginTop: '2px' }}>
                          {h.old_status && <span className={`badge ${STATUS_BADGE[h.old_status] || 'badge-active'}`} style={{ fontSize: '10px' }}>{STATUS_LABEL[h.old_status] || h.old_status}</span>}
                          {h.old_status && h.new_status && <span style={{ margin: '0 4px' }}>→</span>}
                          {h.new_status && <span className={`badge ${STATUS_BADGE[h.new_status] || 'badge-active'}`} style={{ fontSize: '10px' }}>{STATUS_LABEL[h.new_status] || h.new_status}</span>}
                        </div>
                      )}
                      {h.notes && <div style={{ fontSize: '12px', color: 'var(--gray-mid)', marginTop: '3px', fontStyle: 'italic' }}>"{h.notes}"</div>}
                      <div style={{ fontSize: '11px', color: 'var(--gray-lt)', marginTop: '4px' }}>
                        {fmtDate(h.created_at)}{h.changed_by_name ? ` · ${h.changed_by_name}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── DESKTOP TAGOUT MODAL ── */}
      {showTagout && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowTagout(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title" style={{ color: '#C62828' }}>Tag out tool</span>
              <button className="close-btn" onClick={() => setShowTagout(false)}>×</button>
            </div>
            <div style={{ background: '#FFF8E8', padding: '0.75rem 1.25rem', borderBottom: '1px solid #FFE0A0' }}>
              <strong>{tool.name}</strong>
              <div className="text-sm text-muted">{[tool.branches?.name, tool.department, tool.station_truck, tool.serial_number ? 'S/N: ' + tool.serial_number : null].filter(Boolean).join(' · ')}</div>
            </div>
            <div style={{ background: '#F8F8F8', padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>
              Tagged out by: <strong>{profile?.full_name}</strong>
            </div>
            <div className="modal-body">
              <InlineMessage type={tagoutMsg.type} message={tagoutMsg.message} onDismiss={tagoutMsg.clearMessage} />
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Reason *</label>
                  <select className="form-select" value={tagForm.reason} onChange={e => setTagForm(p => ({ ...p, reason: e.target.value }))}>
                    <option>Damage</option><option>Malfunction</option><option>Scheduled maintenance</option>
                    <option>Failed inspection</option><option>Missing parts</option><option>Safety concern</option><option>Other</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Initial status</label>
                  <select className="form-select" value={tagForm.repair_status} onChange={e => setTagForm(p => ({ ...p, repair_status: e.target.value }))}>
                    <option value="tagged_out">Tagged out — awaiting assessment</option>
                    <option value="in_repair">In repair — sent to shop</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Repair shop (if known)</label>
                  <input className="form-input" value={tagForm.shop_name} onChange={e => setTagForm(p => ({ ...p, shop_name: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Est. repair cost ($)</label>
                  <input className="form-input" type="number" min="0" step="0.01" value={tagForm.estimated_repair_cost} onChange={e => setTagForm(p => ({ ...p, estimated_repair_cost: e.target.value }))} />
                </div>
                <div className="form-group full">
                  <label className="form-label">Issue description *</label>
                  <textarea className="form-textarea" value={tagForm.issue_description} onChange={e => setTagForm(p => ({ ...p, issue_description: e.target.value }))} placeholder="Describe the issue requiring tag-out..." />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowTagout(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={saveTagout} disabled={saving}>{saving ? 'Saving...' : 'Tag out tool'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE TAGOUT FLOW ── */}
      {showMobileTagout && (
        <MobileTagout
          tool={tool}
          onClose={() => setShowMobileTagout(false)}
          onSuccess={async () => { setShowMobileTagout(false); await refreshTool(); }}
        />
      )}
    </div>
  );
}